import { useMemo, useState } from "react";
import { DEFAULT_PAGE_SIZE, SortDirection } from "../types";

export interface TableControls<TSort extends string> {
  sortBy: TSort;
  sortDir: SortDirection;
  page: number;
  pageSize: number;
  setSort: (sortBy: TSort) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

interface UseTableControlsArgs<TSort extends string> {
  defaultSortBy: TSort;
  resetKey: string;
  defaultPageSize?: number;
}

export const useTableControls = <TSort extends string>({
  defaultSortBy,
  resetKey,
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: UseTableControlsArgs<TSort>): TableControls<TSort> => {
  const [sort, setSortState] = useState<{ sortBy: TSort; sortDir: SortDirection }>({
    sortBy: defaultSortBy,
    sortDir: "desc",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Reset to page 1 when the shared filters change
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setPage(1);
  }

  const setSort = (nextSortBy: TSort) => {
    setSortState(prev => ({
      sortBy: nextSortBy,
      sortDir: prev.sortBy === nextSortBy && prev.sortDir === "desc" ? "asc" : "desc",
    }));
    setPage(1);
  };

  const setPageSizeAndReset = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  return useMemo(
    () => ({ sortBy: sort.sortBy, sortDir: sort.sortDir, page, pageSize, setSort, setPage, setPageSize: setPageSizeAndReset }),
    [sort, page, pageSize]
  );
};
